import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Form from "./Form";
import dayjs from "dayjs";
import { estadoAusenciaForDropdown, formatCurrency, getLink, getModalidadAusencia, modalidadAusenciaForDropdown, permisions, toast, validarPINSeguridad } from "../../utils/helpers";
import Select from "react-select";
import useFilters from "../../hooks/useFilters";
import { downloadFile, request } from "../../utils/request";
import FiltersContainer from "../general/FiltersContainer";
import Loader from "../general/Loader";
import Pagination from "../general/Pagination";
import Modal from "../general/Modal";
import { apiRequest } from "../../hooks/apiRequest";
import Dropdown from "../general/Dropdown";
import { auth } from "../../utils/auth";
import FormGroup from "./FormGroup";

const renderValues = (value, ausencia, pendiente) => {
    if (pendiente) return ausencia.estado === 'Pendiente' ? value : '-';

    return ausencia.estado !== 'Pendiente' ? value : '-';
};

const tiempoTrabajando = (ingreso) => {
    const start = dayjs(ingreso);
    const end = dayjs();

    const years = end.diff(start, "year");
    const months = end.diff(start.add(years, "year"), "month");

    return `${years} años y ${months} meses`;
};

const calcularMonto = (empleado, cantidad_dia) => {
    const sueldoDia = parseFloat(empleado.sueldoFijo) / 23.83;

    return formatCurrency(sueldoDia * cantidad_dia);
};

const Ausencias = () => {
    const estadoDefaultValue = [estadoAusenciaForDropdown[3]];

    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showColectivasModal, setShowColectivasModal] = useState(false);
    const [ausencias, setAusencias] = useState();
    const [ausencia, setAusencia] = useState({});
    const [empleado, setEmpleado] = useState(null);
    const [ausenciaParaBorrar, setAusenciaParaBorrar] = useState({});
    const [categoriasForDropdown, setCategoriasForDropdown] = useState([]);
    const [pinSeguridad, setPinSeguridad] = useState("");
    const [tipoAusenciasForDropdown, setTipoAusenciasForDropdown] = useState([]);
    const [cargoEmpleadosForDropdown, setCargoEmpleadosForDropdown] = useState([]);
    const [gruposEmpleadosForDropdown, setGruposEmpleadosForDropdown] = useState([]);
    const [showPendienteCols, setShowPendienteCols] = useState(true);
    const [showVacacionesCols, setShowVacacionesCols] = useState(false);
    const [showAllCols, setShowAllCols] = useState(false);

    const { filters, handleFilterChange, filtersQuery } = useFilters({
		desde: null, hasta: null, modalidad: null, tipo: null, estado: estadoDefaultValue.map(item => item.value).join(','), categoria: null,
	});

    const getTipos = async () => {
        const response = await request("tipo-ausencia");
        
        setTipoAusenciasForDropdown(response.data.filter((tipo) => tipo.nombre === "Vacaciones").map((tipo) => ({ value: tipo.id, label: tipo.nombre })));
    };

    const openCreate = (initialState = null) => {
        setAusencia(initialState ?? {
            desde: null,
            modalidad: null,
            cantidad_dia: null,
            contar_sabado: null,
            monto_pagar: null,
            descripcion: null,
            son_adquiridos: null,
            descontar_vacaciones: null,
            tipo_ausencia_id: null,
            empleado: null,
         });

        setShowModal(true);
    };

    const openEdit = (ausencia) => {
        setAusencia({ ...ausencia });

        setShowModal(true);
    };

    const editarOProgramar = (ausencia) => {
        if(ausencia.estado !== 'Pendiente') return openEdit(ausencia);
        
        const tipo = tipoAusenciasForDropdown?.find(option => option.label === "Vacaciones");

        setEmpleado(ausencia.empleado);

        openCreate({
            desde: null,
            modalidad: null,
            cantidad_dia: ausencia.cantidad_dia,
            contar_sabado: null,
            monto_pagar: null,
            descripcion: null,
            son_adquiridos: 0,
            descontar_vacaciones: 1,
            tipo_ausencia_id: tipo.value,
            tipo: { id: tipo.value, nombre: tipo.label },
            empleado: ausencia.empleado ? ausencia.empleado : null,
            estado: ausencia.estado
        });
    };

    const closeModal = () => {
        setAusencia({});
        setEmpleado(null);

        setShowModal(false);
    };

    const openDelete = (ausencia) => {
        setAusenciaParaBorrar({ ...ausencia });

        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setAusenciaParaBorrar({});

        setShowDeleteModal(false);
    };

    const openCreateCollectiveVacation = () => {
        setShowColectivasModal(true);
    };

    const closeCreateCollectiveVacation = () => {
        setShowColectivasModal(false);
    };

    const getAusencias = async (url) => {
        url = url ? url + "&limit=15&clasificacion=vacaciones" : `ausencias?limit=15&clasificacion=vacaciones`;

        setLoading(true);

        setAusencias();

		const response = await request(url);

		setAusencias(response);

		setLoading(false);
    };

    const success = (data, nueva) => {
        closeModal();

        if (nueva) openCreate();

        getAusencias(`ausencias${filtersQuery}`);

    };

    const successColectivas = () => {
        closeCreateCollectiveVacation();

        getAusencias(`ausencias${filtersQuery}`);
    };

    const cancel = async () => {
        setDeleting(true);

        if (await validarPINSeguridad(pinSeguridad)) {
            setPinSeguridad("");

            const response = await apiRequest({}, `ausencias/${ausenciaParaBorrar.id}`, 'delete');

            if (response.success) {
                toast("Ausencia Cancelada", "success");
                
                closeDeleteModal();
                
                closeModal();

                await getAusencias(`ausencias${filtersQuery}`);
            } else {
                toast(response.data.message, "error");
            }
        } else {
            toast("PIN incorrecto, intente de nuevo", "error");
        }

        setDeleting(false);
    };

    const getCategorias = async () => {
        const response = await request("taxonomias?relacion=empleados&buscar=");

        const categorias = response.data.map((categoria) => ({ value: categoria.id, label: categoria.nombre }));
        categorias.unshift({ value: "Todos", label: "Todos" })
        categorias.push({ value: "", label: "Sin Categoría" });

        setCategoriasForDropdown(categorias);
    };

    const getCargosEmpleados = async () => {
        const cargos = await request("tipos");

        if (cargos.data && cargos.data.length) {
            setCargoEmpleadosForDropdown(cargos.data);
        }
    };

    const getGruposEmpleados = async () => {
        const cargos = await request("grupos-tipos-empleados?estado=activo");

        if (cargos.data && cargos.data.length) {
            setGruposEmpleadosForDropdown(cargos.data);
        }
    };

    useEffect(() => {
        getTipos();
        getCargosEmpleados();
        getGruposEmpleados();
        getCategorias();
    }, []);

    useEffect(() => {
        getAusencias(`ausencias${filtersQuery}`);
    }, [filtersQuery]);

    useEffect(() => setPinSeguridad(""), [showDeleteModal]);

    useEffect(() => {
        const ausenciaTodasPendientes = ausencias?.data?.every(ausencia => ausencia.estado === 'Pendiente') ?? false;
        const ausenciaAlgunaPendientes = ausencias?.data?.some(ausencia => ausencia.estado === 'Pendiente') ?? false;
        
        if (ausencias?.data?.length) {
            setShowPendienteCols(ausenciaAlgunaPendientes);
            setShowVacacionesCols(!ausenciaTodasPendientes);
            return;
        }
        
        const estado = filters.estado ? filters.estado.split(',') : [];

        // Todos los estados
        if (estado.length === 0 || estado.includes("Todos")) {
            setShowPendienteCols(true);
            setShowVacacionesCols(true);
            return;
        }

        setShowPendienteCols(estado.includes("Pendiente"));
        setShowVacacionesCols(estado.some(value => value !== 'Pendiente'));
    }, [filters.estado, ausencias]);

    useEffect(() => {
        setShowAllCols(showPendienteCols && showVacacionesCols);
    }, [showPendienteCols, showVacacionesCols]);
    
    return (
        <>
            <div className='page-header'>
                <h1 className='page-title row'>
                    <span className='col'>Vacaciones</span>

                    {auth.can(permisions.reporteAusenciasVacaciones) && <Link
                        className='au-btn au-btn-icon au-btn-filter au-btn--small mr-2'
                        to={getLink(
                            `/imprimir/vacaciones${filtersQuery}&clasificacion=vacaciones`
                            )}
                        target='_blank'>
                        <i className='zmdi zmdi-print' />
                        Imprimir
                    </Link>}

                    {auth.canEdit(permisions.verAusencias) && <>
                        <button
                            className='au-btn au-btn-icon au-btn--green au-btn--small'
                            onClick={() => editarOProgramar({estado: "Pendiente"})}>
                            <i className='zmdi zmdi-plus' />
                            NUEVA VACACIONES
                        </button>

                        <button
                            className='au-btn au-btn-icon au-btn--green au-btn--small ml-2'
                            onClick={openCreateCollectiveVacation}>
                            <i className="fas fa-plus-circle"></i>
                            VACACIONES COLECTIVAS
                        </button>
                    </>}
                </h1>
            </div>

            <FiltersContainer extraFilters={<>
                <div className='col'>
                    <label htmlFor='estado'>Categoría</label>
                    <Select
                        name='tipo'
                        isClearable={true}
                        onChange={(e) => handleFilterChange("categoria", e?.value)}
                        options={categoriasForDropdown}
                    />
                </div>

                <div className='col'>
                    <label htmlFor='cargo'>Cargo</label>
                    <Select
                        name='cargo'
                        id='cargo'
                        onChange={(e) => handleFilterChange("cargo", e.value) }
                        options={[
                            {value: '', label: 'Todos'},
                            ...cargoEmpleadosForDropdown.map(cargo => ({value: cargo.id, label: cargo.nombre}))
                        ]}
                    />
                </div>

                <div className='col'>
                    <label htmlFor='tipo'>Grupo</label>
                    <Select
                        name='grupo'
                        id='grupo'
                        onChange={(e) => handleFilterChange("grupo", e.value)}
                        options={[
                            {value: '', label: 'Todos'},
                            ...gruposEmpleadosForDropdown.map(tipo => ({value: tipo.id, label: tipo.nombre}))
                        ]}
                    />
                </div>

                <div className='col'>
                    <label>Sexo</label>
                    <Select
                        name='sexo'
                        isClearable
                        options={[
                            {label: 'Todos', value: 'todos'},
                            {label: 'Masculino', value: 'M'},
                            {label: 'Femenino', value: 'F'},
                        ]}
                        onChange={(e) => handleFilterChange("sexo", e.value)}
                    />
                </div>
            </>}>
                <div className='col'>
                    <label htmlFor='search'>Buscar</label>
                    <input
                        name='buscar'
                        onChange={(e) => handleFilterChange("buscar", e?.target.value)}
                        type='search'
                        placeholder='Buscar...'
                        className='form-control'
                    />
                </div>

                <div className='col'>
                    <label htmlFor='estado'>Estado</label>
                    <Select
                        name='estado'
                        isClearable={true}
                        isMulti
                        onChange={(e) => handleFilterChange("estado", e)}
                        options={estadoAusenciaForDropdown}
                        defaultValue={estadoDefaultValue}
                    />
                </div>

                <div className='col'>
                    <label htmlFor='modalidad'>Modalidad</label>
                    <Select
                        name='modalidad'
                        isClearable={true}
                        onChange={(e) => handleFilterChange("modalidad", e?.value)}
                        options={modalidadAusenciaForDropdown}
                    />
                </div>

                <div className='col'>
                    <label htmlFor='desde'>Desde</label>
                    <input onChange={(e) => handleFilterChange("desde", e.target.value)}
                        type="date"
                        className='form-control'
                        name='desde'
                        id="desde"
                    />
                </div>

                <div className='col'>
                    <label htmlFor='hasta'>Hasta</label>
                    <input onChange={(e) => handleFilterChange("hasta", e.target.value)}
                        type="date"
                        className='form-control'
                        name='hasta'
                        id="hasta"
                    />
                </div>
            </FiltersContainer>

            <div className='row'>
                <div className='col-md-12'>
                    <div>
                        <div className='table-data' style={{overflowX: 'auto'}}>
                            <table className='table table-data2'>
                                <thead>
                                    <tr>
                                        <th colSpan={2}>Empleado</th>
                                        {showPendienteCols && !showVacacionesCols && <th>Fecha de Ingreso</th>}
                                        {showPendienteCols && !showVacacionesCols && <th>Tiempo Trabajando</th>}
                                        {showPendienteCols && !showVacacionesCols && <th>Dias Disponibles</th>}
                                        {showPendienteCols && !showVacacionesCols && <th>Monto a Pagar</th>}

                                        {showVacacionesCols && <th>Modalidad</th>}
                                        {showVacacionesCols && <th>Desde</th>}
                                        {showVacacionesCols && <th>Hasta</th>}
                                        {showVacacionesCols && <th style={{width: '60px', padding: '10px 5px'}}>Días</th>}
                                        {showVacacionesCols && !showAllCols && <th>Sábado</th>}
                                        {showVacacionesCols && <th>Pagar</th>}
                                        {showVacacionesCols && !showAllCols && <th>Descripción</th>}
                                        {showVacacionesCols && !showAllCols && <th>Desc.</th>}
                                        <th>Estado</th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading
                                        ? (<tr className="tr-shadow"><td colSpan={4 + (showPendienteCols ? 4 : 0) + (showVacacionesCols ? 8 : 0)}><Loader /></td></tr>)
                                        : (ausencias?.data?.length
                                            ? <>
                                                {ausencias.data.map((ausencia) => (
                                                    <tr className="tr-shadow" key={ausencia.id}>
                                                        <td style={{maxWidth: '70px', padding: '5px', overflow: 'hidden'}}>
                                                            {ausencia.empleado?.foto && (
                                                                <span className='page-title-foto'>
                                                                    <img
                                                                        src={ausencia.empleado.foto}
                                                                        alt={ausencia.empleado.codigo}
                                                                    />
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {ausencia.empleado?.nombreCompleto}
                                                            {ausencia.estado !== "Pendiente" && <>
                                                                <br/>
                                                                <small>{tiempoTrabajando(ausencia.empleado?.fechaIngreso)}</small>
                                                            </>}
                                                        </td>
                                                        {showPendienteCols && !showVacacionesCols && <td>{renderValues(dayjs(ausencia.empleado?.fechaIngreso).format("DD/MM/YYYY"), ausencia, true)}</td>}
                                                        {showPendienteCols && !showVacacionesCols && <td>{renderValues(tiempoTrabajando(ausencia.empleado?.fechaIngreso), ausencia, true)}</td>}
                                                        {showPendienteCols && !showVacacionesCols && <td>{renderValues(ausencia.restantes, ausencia, true)}</td>}
                                                        {showPendienteCols && !showVacacionesCols && <td>{renderValues(calcularMonto(ausencia.empleado, ausencia.restantes), ausencia, true)}</td>}
                                                        
                                                        {showVacacionesCols && <td>{renderValues(getModalidadAusencia(ausencia.modalidad)?.label, ausencia, false)}</td>}
                                                        {showVacacionesCols && <td>{renderValues(dayjs(ausencia.desde).format("DD/MM/YYYY"), ausencia, false)}</td>}
                                                        {showVacacionesCols && <td>{renderValues(dayjs(ausencia.hasta).format("DD/MM/YYYY"), ausencia, false)}</td>}
                                                        {showVacacionesCols && <td style={{width: '60px', padding: '5px'}}>{renderValues(<>
                                                            <span title="Total de días">T-{ausencia.cantidad_dia}</span> <br/>
                                                            <span title="Días pendientes">P-{ausencia.restantes?.toString()}</span></>, ausencia, false)}
                                                        </td>}
                                                        {showVacacionesCols && !showAllCols && <td>{renderValues(ausencia.contar_sabado === 0 ? "No Contar" : (ausencia.contar_sabado === 1 ? "Contar 1 Día" : "Contar 1/2 Día"), ausencia, false)}</td>}
                                                        {showVacacionesCols && <td>{renderValues(formatCurrency(ausencia.monto_pagar), ausencia, false)}</td>}
                                                        {showVacacionesCols && !showAllCols && <td>
                                                            <div  style={{
                                                                maxWidth: '120px',
                                                                padding: '5px',
                                                                overflowWrap: 'break-word',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                maxHeight: 'calc(1.5em * 3.2)',
                                                                display: 'flex',
                                                            }}>
                                                                {renderValues(ausencia.descripcion, ausencia, false)}
                                                            </div>
                                                        </td>}
                                                        {showVacacionesCols && !showAllCols && <td>{renderValues(ausencia.descontar_vacaciones ? "Si" : "No", ausencia, false)}</td>}
                                                        <td style={{padding: '5px'}}>
                                                            { ausencia.estado === 'Pendiente' && <span className="badge badge-secondary p-2">Pendiente</span> }
                                                            { ausencia.estado === 'Programadas' && <span className="badge badge-warning text-white p-2">Programadas</span> }
                                                            { ausencia.estado === 'En curso' && <span className="badge badge-info p-2">En curso</span> }
                                                            { ausencia.estado === 'Completada' && <span className="badge badge-success p-2">Completada</span> }
                                                            { ausencia.estado === 'Cancelada' && <span className="badge badge-danger p-2">Cancelada</span> }
                                                        </td>
                                                        { ausencia.cancelada ? <td></td>
                                                            : <td style={{padding: '5px'}}>
                                                            <div className='table-data-feature'>
                                                                <Dropdown
                                                                    classes={{ button: "w-100", dropdownMenu: "align-right" }}
                                                                    styles={{
                                                                        button: {
                                                                            height: "38px",
                                                                            lineHeight: "0px",
                                                                        },
                                                                        dropdownMenu: {
                                                                            zIndex: "99",
                                                                        },
                                                                    }}
                                                                >
                                                                    {ausencia.estado !== 'Pendiente' && <Link
                                                                        className='dropdown-item'
                                                                        data-toggle='tooltip'
                                                                        data-placement='top'
                                                                        title=''
                                                                        data-original-title='Print'
                                                                        to={getLink(`/imprimir/ausencias/${ausencia.id}`)}
                                                                        target='_blank'>
                                                                        Imprimir
                                                                    </Link>}

                                                                    {auth.canEdit(permisions.verAusencias) && <button
                                                                        className='dropdown-item'
                                                                        data-toggle='tooltip'
                                                                        data-placement='top'
                                                                        title=''
                                                                        data-original-title='Edit'
                                                                        onClick={() => editarOProgramar(ausencia) }>
                                                                        {ausencia.estado !== 'Pendiente' ? "Editar" : "Programar"}
                                                                    </button>}

                                                                    { ausencia.estado !== 'Completada' && ausencia.estado !== 'Pendiente' && <>
                                                                        <button
                                                                            className='dropdown-item'
                                                                            data-toggle='tooltip'
                                                                            data-placement='top'
                                                                            title=''
                                                                            data-original-title='Cancel'
                                                                            onClick={() => openDelete(ausencia) }>
                                                                            Cancelar
                                                                        </button>
                                                                    </> }

                                                                    {ausencia.documentos?.length >= 1 && <li className="dropdown-divider"></li>}
                                                                    {ausencia.documentos?.map(documento => (
                                                                        <a onClick={() => {downloadFile(documento.url, documento.nombre ?? `documento_${documento.id}`)}} className="dropdown-item" rel="noreferrer">
                                                                            {documento.nombre ?? `documento_${documento.id}`}
                                                                        </a>
                                                                    ))}
                                                                </Dropdown>
                                                            </div>
                                                        </td> }
                                                    </tr>)
                                                )}

                                                <tr style={{background: "#f7f7f7"}}><td colSpan={4 + (showPendienteCols ? 4 : 0) + (showVacacionesCols ? 8 : 0)}></td></tr>
                                            </>
                                            : (<tr className="tr-shadow"><td colSpan={4 + (showPendienteCols ? 4 : 0) + (showVacacionesCols ? 8 : 0)}>No hay ninguna Ausencia creada.</td></tr>)
                                        )
                                    }
                                </tbody>
                            </table>

                            {ausencias?.links && (<Pagination links={ausencias.links} meta={ausencias.meta} getData={getAusencias} />)}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                show={showModal}
                blank={true}
                toggle={() => setShowModal((state) => !state)}>
                <Form
                    ausencia={ausencia}
                    cancel={closeModal}
                    success={success}
                    ondelete={openDelete}
                    esVacaciones={true}
                    empleado={empleado}
                />
            </Modal>

            <Modal
                title="Cancelar Ausencia"
                show={showDeleteModal}
                blank={false}
                callback={cancel}
                toggle={() => setShowDeleteModal((state) => !state)}>
                    {
                        deleting 
                        ? <><Loader /></> 
                        : <>
                            <p className="px-3 pb-3">¿Estás seguro que desea cancelar esta ausencia? (Esta acción no se puede deshacer).</p>
                    
                            <div className="form-group">
                                <label>PIN de seguridad</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="PIN"
                                    name="pin_seguridad"
                                    value={pinSeguridad}
                                    onChange={(e) =>
                                        setPinSeguridad(e.target.value)
                                    }
                                />
                            </div>
                        </>
                    }
            </Modal>

            <Modal
                size="lg"
                show={showColectivasModal}
                blank={true}
                toggle={() => setShowColectivasModal((state) => !state)}>
                <FormGroup filtersQuery={filtersQuery} cancel={closeCreateCollectiveVacation} success={successColectivas}></FormGroup>
            </Modal>
        </>
    );
};

export default Ausencias;
